/**
 * VariableIdeasPanel — T8.
 *
 * Collapsed-by-default panel that lets writers generate AI-suggested brief
 * variable sets (angle / avatar / problem mechanism / solution mechanism /
 * key ingredients / awareness level) for the selected product + market.
 *
 * Props:
 *   productId    — from the BriefForm's selected product (null = not yet selected)
 *   market       — from the BriefForm's selected market (null/'' = not yet selected)
 *   partialBrief — optional snapshot of currently filled brief fields; sent to
 *                  the ideation endpoint for steering. Component-local — no store access.
 *   onApplyIdea  — called when the user clicks "Use this set". Parent is responsible
 *                  for calling the store's applyIdeaSet action. Keeps this component
 *                  pure and testable without the store.
 *
 * The component itself does NOT touch the brief-form-store. It receives data in
 * via props and emits decisions out via onApplyIdea.
 *
 * Cost is intentionally never shown to the writer — it is recorded in the
 * ideation_requests audit table for system_owner queries only.
 */
import type { GenerationModelKey, IdeateRequest, ParsedIdea } from '@malaberg/shared';
import {
  Alert,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconChevronDown, IconChevronUp, IconSparkles } from '@tabler/icons-react';
import { useState } from 'react';
import { useIdeate } from '../../hooks/use-ideate.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface VariableIdeasPanelProps {
  /** Selected product ID from the brief form. null = no product chosen yet. */
  productId: string | null;
  /** Selected market from the brief form. null/'' = no market chosen yet. */
  market: string | null;
  /** Selected generation model for both Variable Ideas and final generation. */
  generationModelKey: GenerationModelKey;
  /**
   * Optional snapshot of currently filled brief fields. Passed through to the
   * ideation endpoint for steering (does not affect UI state).
   */
  partialBrief?: IdeateRequest['partialBrief'];
  /**
   * Called when the user clicks "Use this set" on an idea card.
   * The parent (BriefForm) is responsible for calling the store's applyIdeaSet.
   */
  onApplyIdea: (idea: ParsedIdea) => void;
}

// ─── Idea Card ────────────────────────────────────────────────────────────────

interface IdeaCardProps {
  idea: ParsedIdea;
  index: number;
  onApply: (idea: ParsedIdea) => void;
}

function IdeaCard({ idea, index, onApply }: IdeaCardProps) {
  const humanIndex = index + 1;
  const title = idea.title || idea.angle || 'Variable set';

  return (
    <Card withBorder padding="sm" radius="sm" data-testid={`variable-ideas-card-${index}`}>
      {/* Card header: index + title */}
      <Text fw={600} mb="xs">
        {humanIndex}. {title}
      </Text>

      {/* 6 labeled fields rendered as a definition list */}
      <Box component="dl" style={{ margin: 0 }}>
        <IdeaField label="Angle" value={idea.angle} />
        <IdeaField label="Avatar" value={idea.avatar} />
        <IdeaField label="Problem mechanism" value={idea.problemMechanism} />
        <IdeaField label="Solution mechanism" value={idea.solutionMechanism} />
        <IdeaField label="Ingredients" value={idea.keyIngredients} />
        <IdeaField label="Awareness" value={idea.awarenessLevel} />
      </Box>

      {/* Why it works — italic dimmed paragraph */}
      {idea.whyItWorks && (
        <Text size="sm" c="dimmed" fs="italic" mt="xs">
          {idea.whyItWorks}
        </Text>
      )}

      {/* Footer: apply button */}
      <Button
        size="sm"
        variant="light"
        mt="sm"
        // Accessible label includes index + title for screen readers
        aria-label={`Use variable set ${humanIndex}: ${title}`}
        onClick={() => onApply(idea)}
      >
        Use this set
      </Button>
    </Card>
  );
}

interface IdeaFieldProps {
  label: string;
  value: string;
}

function IdeaField({ label, value }: IdeaFieldProps) {
  return (
    <>
      <Text component="dt" size="xs" c="dimmed" fw={500} mt={4}>
        {label}
      </Text>
      <Text component="dd" size="sm" style={{ margin: 0 }}>
        {value}
      </Text>
    </>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function VariableIdeasPanel({
  productId,
  market,
  generationModelKey,
  partialBrief,
  onApplyIdea,
}: VariableIdeasPanelProps) {
  const [opened, setOpened] = useState(false);
  // Direction state is component-local — transient steering, NOT in the global store.
  const [direction, setDirection] = useState('');

  const { status, ideas, error, suggest } = useIdeate();

  // Derive readable helpers
  const isLoading = status === 'loading';
  const isDisabled = !productId || !market || isLoading;
  const ideaCount = ideas?.length ?? 0;

  // Unique id for the collapsible body (used for aria-controls)
  const bodyId = 'variable-ideas-body';

  // Build and fire the ideation request
  const handleSuggest = () => {
    if (isDisabled) return;

    const request: IdeateRequest = {
      productId: productId as string,
      generationModelKey,
      market: market as string,
      // Only include direction when the writer typed something
      ...(direction.trim() ? { direction: direction.trim() } : {}),
      // Include partial brief snapshot for steering when provided
      ...(partialBrief ? { partialBrief } : {}),
    };

    void suggest(request);
  };

  return (
    <Card withBorder padding="sm" radius="sm" data-testid="variable-ideas-panel">
      {/* ── Collapsible header ──────────────────────────────────────────────── */}
      <button
        type="button"
        aria-expanded={opened}
        aria-controls={bodyId}
        onClick={() => setOpened((prev) => !prev)}
        data-testid="variable-ideas-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px 0',
          textAlign: 'left',
        }}
      >
        <Group gap="xs">
          <IconSparkles size={16} />
          <Text fw={500} size="sm">
            Variable Ideas
            {ideaCount > 0 && (
              <Text component="span" c="dimmed" fw={400}>
                {' '}
                ({ideaCount} suggestion{ideaCount !== 1 ? 's' : ''})
              </Text>
            )}
          </Text>
        </Group>
        {opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
      </button>

      {/* ── Collapsible body ─────────────────────────────────────────────────── */}
      <Collapse in={opened}>
        <Stack gap="sm" mt="sm" id={bodyId}>
          {/* Direction text input — transient steering, component-local state */}
          <TextInput
            label="Direction (optional)"
            placeholder="Weak legs, belly fat, blue collar, doctor distrust"
            value={direction}
            onChange={(e) => setDirection(e.currentTarget.value)}
            data-testid="variable-ideas-direction"
          />

          {/* Suggest Sets button */}
          <Tooltip label="Select a product and market first" disabled={!!productId && !!market}>
            <Button
              variant="filled"
              disabled={isDisabled}
              loading={isLoading}
              onClick={handleSuggest}
              data-testid="variable-ideas-suggest-btn"
            >
              {isLoading ? 'Thinking...' : 'Suggest Sets'}
            </Button>
          </Tooltip>

          {/* Error state */}
          {status === 'error' && error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Ideation failed">
              <Stack gap="xs">
                <Text size="sm">{error.message}</Text>
                <Button size="xs" variant="subtle" color="red" onClick={handleSuggest}>
                  Try again
                </Button>
              </Stack>
            </Alert>
          )}

          {/* Idle state helper text */}
          {status === 'idle' && ideaCount === 0 && (
            <Text size="sm" c="dimmed">
              Click Suggest Sets to generate variable sets for this product
            </Text>
          )}

          {/* Success state — idea cards */}
          {status === 'success' && ideas && ideas.length > 0 && (
            <Stack gap="sm">
              {ideas.map((idea, index) => (
                <IdeaCard
                  key={`${idea.title}-${index}`}
                  idea={idea}
                  index={index}
                  onApply={onApplyIdea}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Collapse>
    </Card>
  );
}
